// src/services/auth.service.ts
import { getAuth, signInWithCustomToken } from "firebase/auth";

const API_URL = `${process.env.REACT_APP_API_URL}/api/auth`;

/**
 * Signup with email/password via backend
 */
export const signupWithEmail = async (
  email: string,
  password: string,
  organisationId: string
) => {
  try {
    const res = await fetch(`${API_URL}/signup`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, organisationId }),
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, message: err.message };
  }
};

/**
 * Sign in with email/password via backend
 */
export const signInWithEmail = async (email: string, password: string) => {
  try {
    // Step 1: Get custom token from your backend
    const res = await fetch(`${API_URL}/signin`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    const { success, token, message } = await res.json();
    if (!success) return { success, message };

    // Step 2: Sign in Firebase client with custom token
    const auth = getAuth();
    await signInWithCustomToken(auth, token);

    // Step 3: Get ID token for API requests
    const idToken = await auth.currentUser!.getIdToken();

    return { success: true, user: auth.currentUser, idToken };
  } catch (err: any) {
    return { success: false, message: err.message };
  }
};

/*
 * Sign out the current user (client-side only)
 */
export const signOutUser = async () => {
  try {
    const auth = getAuth();
    await auth.signOut();
    return { success: true };
  } catch (err: any) {
    return { success: false, message: err.message };
  }
};

/**
 * Start Google OAuth via backend
 */
export const signInWithGoogle = () => {
  // Redirect browser to backend OAuth endpoint
  window.location.href = `${API_URL}/google`;
};

/**
 * Handle Firebase custom token from Google OAuth callback
 * Call this on your /auth/success page
 */

export const handleGoogleCallback = async () => {
  const auth = getAuth();
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get("token");

  if (!token) {
    return { success: false, message: "No token found in URL" };
  }

  try {
    // Sign in Firebase client with custom token
    await signInWithCustomToken(auth, token);

    // Get ID token for backend API requests
    const idToken = await auth.currentUser!.getIdToken();

    return { success: true, user: auth.currentUser, idToken };
  } catch (err: any) {
    return { success: false, message: "Invalid Firebase token" };
  }
};
